import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileSpreadsheet, Upload, Loader2, CheckCircle, AlertCircle, Building2, BarChart3, FileDown, X, Layers } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectQuantityComparison = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file => 
      file.name.endsWith('.xlsx') || file.name.endsWith('.xls')
    );
    
    if (validFiles.length !== acceptedFiles.length) {
      toast.error('بعض الملفات غير مدعومة - يجب رفع ملفات Excel فقط');
    }
    
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      setResult(null);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: true
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('الرجاء اختيار ملف واحد على الأقل');
      return;
    }

    if (files.length < 2) {
      toast.warning('يُفضل رفع ملفين على الأقل للمقارنة');
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_URL}/api/analysis/project-quantity/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        toast.success(`تم تحليل ${data.total_items} صنف من ${data.projects_count} مشروع`);
      } else {
        throw new Error(data.detail || 'فشل في تحليل الملفات');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    if (!result?.task_id) {
      toast.error('الرجاء رفع وتحليل الملفات أولاً');
      return;
    }

    setExporting(true);

    try {
      const response = await fetch(`${API_URL}/api/analysis/project-quantity/export-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ task_id: result.task_id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'فشل في تصدير الملف');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `مقارنة_كميات_المشاريع_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('تم تصدير التقرير بنجاح');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFiles([]);
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 p-6" dir="rtl">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 mb-4 shadow-lg shadow-emerald-500/25">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">مقارنة كميات المشاريع</h1>
          <p className="text-slate-400">رفع ملفات كميات المشاريع للمقارنة والتحليل</p>
        </div>

        {/* Info Box */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 mb-6 border border-slate-700">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">مقارنة متقدمة للمشاريع</h3>
              <p className="text-slate-400 text-sm">
                ارفع ملفات كميات المشاريع المختلفة لمقارنة الأصناف والكميات والأسعار
              </p>
              <p className="text-slate-500 text-xs mt-1">
                * اسم المشروع سيؤخذ من اسم الملف المرفوع
              </p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 mb-6">
          <div
            {...getRootProps()}
            data-testid="project-qty-dropzone"
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
              isDragActive
                ? 'border-emerald-500 bg-emerald-500/10'
                : files.length > 0
                ? 'border-emerald-500/50 bg-emerald-500/5'
                : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/30'
            }`}
          >
            <input {...getInputProps()} data-testid="project-qty-file-input" />
            
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-xl bg-slate-700 flex items-center justify-center mb-3">
                <Upload className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-white font-medium mb-1">
                {isDragActive ? 'أفلت الملفات هنا' : 'اسحب ملفات كميات المشاريع هنا'}
              </p>
              <p className="text-slate-400 text-sm">أو اضغط لاختيار ملفات</p>
              <p className="text-slate-500 text-xs mt-2">يدعم رفع عدة ملفات (.xlsx و .xls) - كل ملف = مشروع</p>
            </div>
          </div>

          {/* Files List */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-slate-400 text-sm mb-2">المشاريع المختارة ({files.length}):</p>
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm">{file.name.replace(/\.(xlsx|xls)$/i, '')}</p>
                      <p className="text-slate-500 text-xs">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-slate-600 rounded transition-colors"
                    data-testid={`remove-file-${index}`}
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
              ))}
              
              <button
                onClick={resetForm}
                className="text-sm text-slate-400 hover:text-white transition-colors mt-2"
              >
                إزالة جميع الملفات
              </button>
            </div>
          )}

          {/* Upload Button */}
          {files.length > 0 && !result && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full mt-4 py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="project-qty-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري التحليل...
                </>
              ) : (
                <>
                  <Layers className="w-5 h-5" />
                  تحليل ومقارنة المشاريع
                </>
              )}
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">خطأ في التحليل</p>
              <p className="text-red-300/70 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 space-y-6">
            {/* Success Header */}
            <div className="flex items-center gap-3 pb-4 border-b border-slate-700">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-medium">تم التحليل بنجاح</p>
                <p className="text-slate-400 text-sm">جاهز لتصدير تقرير المقارنة</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-400">{result.projects_count}</p>
                <p className="text-slate-400 text-sm">مشروع</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{result.total_items}</p>
                <p className="text-slate-400 text-sm">إجمالي الأصناف</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-purple-400">{result.common_items}</p>
                <p className="text-slate-400 text-sm">أصناف مشتركة</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{result.unique_items}</p>
                <p className="text-slate-400 text-sm">أصناف فريدة</p>
              </div>
            </div>

            {/* Projects List */}
            {result.projects && result.projects.length > 0 && (
              <div className="space-y-2">
                <p className="text-slate-400 text-sm">المشاريع:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {result.projects.map((project, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="text-white text-sm">{project.name}</p>
                        <p className="text-slate-500 text-xs">{project.items_count} صنف • {project.total_amount?.toLocaleString()} ر.س</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={exporting}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="project-qty-export-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري التصدير...
                </>
              ) : (
                <>
                  <FileDown className="w-5 h-5" />
                  تصدير تقرير المقارنة (Excel)
                </>
              )}
            </button>

            {/* Reset */}
            <button
              onClick={resetForm}
              className="w-full py-2 text-slate-400 hover:text-white transition-colors text-sm"
            >
              تحليل ملفات جديدة
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectQuantityComparison;
